import { useState, useRef } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API } from "../lib/api";
import { RevealLines } from "../components/Reveal";
import { Sparkles, Loader2, Upload, FileCheck } from "lucide-react";

const EXEC_IMG = "https://static.prod-images.emergentagent.com/jobs/01bf850e-1aa5-4eff-9307-bea3aa5e9f7d/images/6b749fc2e112f7b25bda872180af8abff2c6e2d81dc678b2f373bb291e517fac.jpeg";
const AI_VIZ = "https://static.prod-images.emergentagent.com/jobs/01bf850e-1aa5-4eff-9307-bea3aa5e9f7d/images/582f4cf6ff3ed7baac2cd33f446fec00fb5a46d5e095f1f9d37b295df1737192.jpeg";

function renderMarkdown(md) {
  const lines = md.split("\n");
  const html = [];
  let inList = false;
  const inline = (s) => s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  for (let line of lines) {
    if (line.startsWith("## ")) {
      if (inList) { html.push("</ul>"); inList = false; }
      html.push(`<h2>${inline(line.slice(3))}</h2>`);
    } else if (line.trim().startsWith("- ") || line.trim().startsWith("* ")) {
      if (!inList) { html.push("<ul>"); inList = true; }
      html.push(`<li>${inline(line.trim().slice(2))}</li>`);
    } else if (line.trim() === "") {
      if (inList) { html.push("</ul>"); inList = false; }
    } else {
      if (inList) { html.push("</ul>"); inList = false; }
      html.push(`<p>${inline(line)}</p>`);
    }
  }
  if (inList) html.push("</ul>");
  return html.join("");
}

export default function ResumeReview() {
  const [resume, setResume] = useState("");
  const [role, setRole] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);
  const outRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const n = file.name.toLowerCase();
    if (!n.endsWith(".pdf") && !n.endsWith(".docx")) { toast.error("Please upload a PDF or Word (.docx) file."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/ai/resume-parse`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Could not read the PDF."); return; }
      setResume(data.text);
      setFileName(data.filename);
      toast.success(`Imported ${data.pages} page${data.pages > 1 ? "s" : ""} from ${data.filename}`);
    } catch (_) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const run = async () => {
    if (resume.trim().length < 40) { setOutput(""); return; }
    setLoading(true);
    setOutput("");
    try {
      const res = await fetch(`${API}/ai/resume-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resume_text: resume, target_role: role || null }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); setOutput(`**${e.detail || "Something went wrong."}**`); setLoading(false); return; }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setOutput(acc);
        outRef.current?.scrollTo({ top: outRef.current.scrollHeight });
      }
    } catch (_) {
      setOutput("**We hit a snag. Please try again.**");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-cream min-h-screen pt-[72px]" data-testid="resume-review-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-28">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="overline text-cobalt">Free AI Tool</span>
              <span className="h-px w-14 bg-navy/30" />
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-navy leading-[0.95]">
              <RevealLines lines={["Instant resume", "review, on us."]} />
            </h1>
            <p className="text-slateink text-lg mt-6 max-w-xl">
              Upload your PDF or Word resume — or paste the text — and get a senior consultant's-grade,
              <span className="text-navy font-semibold"> ATS-based</span> review in seconds: compatibility score,
              keyword gaps and rewrite-level fixes.
            </p>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="relative">
            <div className="relative overflow-hidden border border-navy/10 bg-navy p-3 shadow-[0_30px_80px_-40px_rgba(10,17,40,0.55)]">
              <img src={EXEC_IMG} alt="Executive reviewing an AI resume dashboard"
                className="w-full h-[300px] lg:h-[380px] object-cover" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/85 backdrop-blur-md border border-white/60 px-5 py-4">
                <p className="font-mono-ui text-[0.62rem] text-cobalt uppercase tracking-widest">AI-Powered · ATS Engine</p>
                <p className="font-display text-lg text-navy mt-1">Reviewed the way recruiters' systems read it</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-14">
          {/* Input */}
          <div className="bg-white border border-border p-7">
            <label className="overline text-slateink block mb-2">Target role (optional)</label>
            <input data-testid="review-role-input" value={role} onChange={(e) => setRole(e.target.value)}
              placeholder="e.g. Senior Backend Engineer"
              className="w-full border border-border px-4 py-3 outline-none focus:border-cobalt text-navy mb-6" />

            <label className="overline text-slateink block mb-2">Paste your resume</label>
            <div className="mb-3">
              <input ref={fileRef} type="file" accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleFile}
                className="hidden" data-testid="review-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                data-testid="review-upload-button"
                className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-navy/30 hover:border-cobalt hover:bg-secondary/50 transition-colors px-4 py-4 text-sm text-navy disabled:opacity-60">
                {uploading ? <><Loader2 size={16} className="animate-spin" /> Reading file…</>
                  : fileName ? <><FileCheck size={16} className="text-cobalt" /> {fileName} · upload another</>
                  : <><Upload size={16} /> Upload PDF or Word (.docx) — we'll ATS-scan it for you</>}
              </button>
            </div>
            <textarea data-testid="review-resume-input" value={resume} onChange={(e) => setResume(e.target.value)} rows={11}
              placeholder="…or paste your resume text here"
              className="w-full border border-border px-4 py-3 outline-none focus:border-cobalt text-navy resize-none text-sm leading-relaxed" />

            <button data-testid="review-submit-button" onClick={run} disabled={loading || resume.trim().length < 40}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-navy text-white font-medium hover:bg-cobalt transition-colors disabled:opacity-50">
              {loading ? <><Loader2 size={18} className="animate-spin" /> Analyzing…</> : <><Sparkles size={18} /> Review my resume</>}
            </button>
          </div>

          {/* Output */}
          <div className="bg-navy text-white grain relative p-7 min-h-[520px] overflow-hidden">
            <img src={AI_VIZ} alt="" aria-hidden="true"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${output ? "opacity-[0.10]" : "opacity-40"}`} />
            <div className="absolute inset-0 bg-navy/70" />
            <div ref={outRef} className="relative z-10 max-h-[560px] overflow-y-auto">
              {output ? (
                <div className="markdown-review text-white/90 [&_h2]:text-white [&_strong]:text-cobalt-light"
                  data-testid="review-output"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(output) }} />
              ) : (
                <div className="h-full grid place-items-center text-center py-24" data-testid="review-placeholder">
                  <div>
                    <Sparkles size={40} className="text-cobalt-light mx-auto mb-4" strokeWidth={1.4} />
                    <p className="text-white/70 max-w-xs mx-auto">Your personalised ATS review will stream here.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
